import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import { PricingPage } from "@/pages/PricingPage";
import { FAQPage } from "@/pages/FAQPage";
import {
  Menu,
  X,
  ArrowRight,
  CheckCircle2,
  TrendingUp,
  Zap,
  Shield,
  BarChart3,
  Clock,
  CreditCard,
  Users,
  FileText,
  RefreshCw,
  ChevronDown,
  Package,
  DollarSign
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { TermsPage } from "./pages/TermsPage";
import { PrivacyPage } from "./pages/PrivacyPage";

const Home = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [demoDialogOpen, setDemoDialogOpen] = useState(false);
  const [demoStatus, setDemoStatus] = useState("");
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [benefitsTab, setBenefitsTab] = useState("distributors");
  const [howItWorksTab, setHowItWorksTab] = useState("distributors");
  const [howTurboPayHelps, setHowTurboPayHelps] = useState("distributors");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: ""
  });
  const BASEURL = process.env.REACT_APP_BASEURL;

  // Hero image carousel
  // const heroImages = [
  //   "https://images.pexels.com/photos/27111449/pexels-photo-27111449.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  //   "https://images.unsplash.com/photo-1739066598279-1297113f5c6a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjBzaG9wa2VlcGVyJTIwc21pbGluZyUyMGluJTIwbW9kZXJuJTIwa2lyYW5hJTIwc3RvcmUlMjBjb3VudGVyfGVufDB8fHx8MTc3MDIyNjI5MXww&ixlib=rb-4.1.0&q=85",
  //   "https://images.pexels.com/photos/906494/pexels-photo-906494.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
  // ];

  const heroImages = [
    "/images/distributor.png",
    "/images/infrastructureVision.jpeg",
    "/images/nbfc.jpeg"
  ];


  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % heroImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    console.log("Demo request:", formData);
    try {
      const res = await fetch(`${BASEURL}/api/WebsiteEmailApi/SendContact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          Name: formData.name,
          Phone: formData.phone,
          Email: formData.email,
          Company: formData.company,
          Message: formData.message
        })
      });
      if (res.status) {
        toast.success("Demo request submitted successfully! We'll contact you soon.");
        setDemoDialogOpen(false);
        setFormData({ name: "", email: "", phone: "", company: "", message: "" });
      }
    } catch (error) {
      console.log(error.stack);
      toast.warning("Error booking a demo. Please try again");
    }
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const heroContent = [
    {
      heading: "Stop Cash Leakage. Cut Collection Time. Unlock Capital.",
      subParagraph: "Distributors adopt TurboPay to digitise collections and eliminate cash leakage. Every transaction your field team records becomes your trade signal — and your pathway to institutional credit.",
      tagLine: "Less paperwork. Zero cash leakage. Capital when you need it.",
      button1: "Get Started as a Distributor ",
      button2: "See How It Works"
    },
    {
      heading: "The Trade Credit Infrastructure for India's Distributors.",
      subParagraph: "Distributors adopt us to digitise collections. We convert every transaction into the underwriting infrastructure that institutional lenders have never had access to before, unlocking scalable, data-driven credit underwriting.",
      tagLine: "Collections is our wedge. Infrastructure is our moat. Credit follows.",
      button1: "Book a Demo ",
      button2: "See How It Works"
    },
    {
      heading: "Deploy Credit Against Live Trade Signals — Not Balance Sheets.",
      subParagraph: "TurboPay gives institutional lenders real-time invoice-level repayment behaviour across a live distributor network. Connect via API. Deploy credit with confidence. Repayments flow directly into your account.",
      tagLine: "See the trade. Underwrite with confidence. Collect automatically.",
      button1: "Partner as a Lender ",
      button2: "See How It Works"
    }
  ]

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="container-custom">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <div className="flex items-center">
              <a href="#" data-testid="logo">
                <img
                  // src="https://customer-assets.emergentagent.com/job_fintech-demo-9/artifacts/hrowc68u_image.png" 
                  src="/images/turbopayUpdatedBig.jpeg"
                  alt="TurboPay"
                  className="h-8 md:h-14 w-auto"
                />
              </a>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8 text-lg">
              {/* <button onClick={() => scrollToSection('solution')} className="text-slate-600 hover:text-[#1F497D] font-medium transition-colors" data-testid="nav-product">
                Product
              </button> */}
              <button onClick={() => scrollToSection('features')} className="text-slate-600 hover:text-[#1F497D] font-medium transition-colors" data-testid="nav-solutions">
                Benefits
              </button>
              <button onClick={() => scrollToSection('how-it-works')} className="text-slate-600 hover:text-[#1F497D] font-medium transition-colors" data-testid="nav-how-it-works">
                How it Works
              </button>
              <Link to="/pricing" className="text-slate-600 hover:text-[#1F497D] font-medium transition-colors" data-testid="nav-pricing">
                Pricing
              </Link>
              <button onClick={() => scrollToSection('testimonials')} className="text-slate-600 hover:text-[#1F497D] font-medium transition-colors" data-testid="nav-customers">
                Customers
              </button>
              <Link to="/faq" className="text-slate-600 hover:text-[#1F497D] font-medium transition-colors" data-testid="nav-faq">
                FAQs
              </Link>
            </div>

            {/* CTA Button */}
            <div className="hidden lg:block">
              <Dialog open={demoDialogOpen} onOpenChange={(open) => { setDemoDialogOpen(open); if (!open) setDemoStatus("") }}>
                <DialogTrigger asChild>
                  <Button className="bg-[#1F497D] text-white hover:bg-[#183a64] h-11 px-8 rounded-full font-semibold shadow-[0_4px_14px_0_rgba(31,73,125,0.3)] transition-all hover:scale-105 active:scale-95" data-testid="nav-get-demo-btn">
                    Get Demo
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[500px]" data-testid="demo-dialog">
                  <DialogHeader>
                    <DialogTitle className="font-outfit text-2xl">{demoStatus === "contact" ? "Contact Us" : "Book a Demo"}</DialogTitle>
                    <DialogDescription>
                      Fill out the form below and we'll get back to you shortly.
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleDemoSubmit} className="space-y-4 mt-4">
                    <div>
                      <Label htmlFor="name">Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleFormChange}
                        className="mt-1.5"
                        data-testid="demo-form-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleFormChange}
                        className="mt-1.5"
                        data-testid="demo-form-email-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={handleFormChange}
                        className="mt-1.5"
                        data-testid="demo-form-phone-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="company">Company</Label>
                      <Input
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleFormChange}
                        className="mt-1.5"
                        data-testid="demo-form-company-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="message">Message</Label>
                      <Textarea
                        id="message"
                        name="message"
                        rows={3}
                        value={formData.message}
                        onChange={handleFormChange}
                        className="mt-1.5"
                        data-testid="demo-form-message-input"
                      />
                    </div>
                    <Button type="submit" className="w-full bg-[#1F497D] hover:bg-[#183a64] h-11 rounded-full font-semibold" data-testid="demo-form-submit-btn">
                      {demoStatus === "contact" ? "Contact Us" : "Book Demo"}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="lg:hidden py-4 border-t border-slate-100" data-testid="mobile-menu">
              <div className="flex flex-col space-y-3">
                {/* <button onClick={() => scrollToSection('solution')} className="text-slate-600 hover:text-[#1F497D] font-medium text-left py-2">
                  Product
                </button> */}
                <button onClick={() => scrollToSection('features')} className="text-slate-600 hover:text-[#1F497D] font-medium text-left py-2">
                  Benefits
                </button>
                <button onClick={() => scrollToSection('how-it-works')} className="text-slate-600 hover:text-[#1F497D] font-medium text-left py-2">
                  How it Works
                </button>
                <Link to="/pricing" className="text-slate-600 hover:text-[#1F497D] font-medium text-left py-2">
                  Pricing
                </Link>
                <button onClick={() => scrollToSection('testimonials')} className="text-slate-600 hover:text-[#1F497D] font-medium text-left py-2">
                  Customers
                </button>
                <Link to="/faq" className="text-slate-600 hover:text-[#1F497D] font-medium text-left py-2">
                  FAQs
                </Link>
                <Dialog open={demoDialogOpen} onOpenChange={setDemoDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="w-full bg-[#1F497D] text-white hover:bg-[#183a64] mt-4" data-testid="mobile-get-demo-btn">
                      Get Demo
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[500px]">
                    <DialogHeader>
                      <DialogTitle className="font-outfit text-2xl">Book a Demo</DialogTitle>
                      <DialogDescription>
                        Fill out the form below and we'll get back to you shortly.
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleDemoSubmit} className="space-y-4 mt-4">
                      <div>
                        <Label htmlFor="mobile-name">Name *</Label>
                        <Input
                          id="mobile-name"
                          name="name"
                          required
                          value={formData.name}
                          onChange={handleFormChange}
                          className="mt-1.5"
                        />
                      </div>
                      <div>
                        <Label htmlFor="mobile-email">Email *</Label>
                        <Input
                          id="mobile-email"
                          name="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={handleFormChange}
                          className="mt-1.5"
                        />
                      </div>
                      <div>
                        <Label htmlFor="mobile-phone">Phone</Label>
                        <Input
                          id="mobile-phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleFormChange}
                          className="mt-1.5"
                        />
                      </div>
                      <div>
                        <Label htmlFor="mobile-company">Company</Label>
                        <Input
                          id="mobile-company"
                          name="company"
                          value={formData.company}
                          onChange={handleFormChange}
                          className="mt-1.5"
                        />
                      </div>
                      <div>
                        <Label htmlFor="mobile-message">Message</Label>
                        <Textarea
                          id="mobile-message"
                          name="message"
                          rows={3}
                          value={formData.message}
                          onChange={handleFormChange}
                          className="mt-1.5"
                        />
                      </div>
                      <Button type="submit" className="w-full bg-[#1F497D] hover:bg-[#183a64] h-11 rounded-full font-semibold">
                        Book Demo
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-gradient py-8 md:py-12 pb-16 md:pb-16" data-testid="hero-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            {/* Left Column - Text Content */}
            <div className="space-y-8">
              <h1 className="font-outfit text-4xl md:text-6xl lg:text-6xl font-bold tracking-tight leading-[1.1] text-slate-900" data-testid="hero-heading">
                {/* Collect Faster. Fund Smarter. Grow Without Limits. */}
                {/* The Cash Flow Engine for Distributors and Retailers */}
                {heroContent[currentImageIndex].heading}
              </h1>
              <p className="text-lg md:text-xl text-slate-600 leading-relaxed" data-testid="hero-subheading">
                {/* TurboPay helps FMCG distributors collect faster and provides instant working capital to both distributors and retailers — all in one platform. */}
                {heroContent[currentImageIndex].subParagraph}
              </p>
              <p className="text-base md:text-lg text-[#1F497D] font-medium" data-testid="hero-supporting-text">
                {/* Digitize collections. Prevent cash leakages. Unlock working capital. */}
                {heroContent[currentImageIndex].tagLine}
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Dialog open={demoDialogOpen} onOpenChange={setDemoDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-[#1F497D] text-white hover:bg-[#183a64] h-12 px-8 rounded-full font-semibold shadow-[0_4px_14px_0_rgba(31,73,125,0.3)] transition-all hover:scale-105 active:scale-95" data-testid="hero-book-demo-btn">
                      {/* Book a Demo */}
                      {heroContent[currentImageIndex].button1}
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </DialogTrigger>
                </Dialog>
                <Button
                  variant="outline"
                  className="bg-white text-slate-900 border border-slate-200 hover:border-[#1F497D] hover:bg-blue-50/50 h-12 px-8 rounded-full font-medium transition-all hover:shadow-sm active:scale-95"
                  onClick={() => scrollToSection('how-it-works')}
                  data-testid="hero-see-how-it-works-btn"
                >
                  {/* See How It Works */}
                  {heroContent[currentImageIndex].button2}
                </Button>
              </div>
            </div>

            {/* Right Column - Image Carousel */}
            <div className="relative h-[300px] md:h-[350px] lg:h-[400px]" data-testid="hero-visual">
              <div className="absolute inset-0 rounded-2xl overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.15)]">
                {heroImages.map((image, index) => (
                  <div
                    key={index}
                    className={`absolute inset-0 transition-opacity duration-1000 ${index === currentImageIndex ? 'opacity-100' : 'opacity-0'
                      }`}
                  >
                    <img
                      src={image}
                      alt={`FMCG business ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
              {/* Carousel indicators */}
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
                {heroImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`w-2 h-2 rounded-full transition-all ${index === currentImageIndex
                      ? 'bg-white w-8'
                      : 'bg-white/50'
                      }`}
                    aria-label={`Go to image ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar - Hidden */}
      <section className="hidden py-16 md:py-20 border-y border-slate-100 bg-slate-50" data-testid="trust-bar">
        <div className="container-custom">
          <p className="text-center text-sm font-medium text-slate-600 mb-12" data-testid="trust-bar-text">
            Trusted by growing FMCG distributors and retailers across India
          </p>
          <div className="flex flex-wrap justify-center items-center gap-12 md:gap-16 grayscale opacity-60">
            {/* Dummy Company Logos */}
            <div className="h-10 w-32 bg-slate-300 rounded flex items-center justify-center">
              <span className="font-semibold text-slate-600 text-xs">COMPANY</span>
            </div>
            <div className="h-10 w-32 bg-slate-300 rounded flex items-center justify-center">
              <span className="font-semibold text-slate-600 text-xs">BRAND</span>
            </div>
            <div className="h-10 w-32 bg-slate-300 rounded flex items-center justify-center">
              <span className="font-semibold text-slate-600 text-xs">PARTNER</span>
            </div>
            <div className="h-10 w-32 bg-slate-300 rounded flex items-center justify-center">
              <span className="font-semibold text-slate-600 text-xs">CLIENT</span>
            </div>
            <div className="h-10 w-32 bg-slate-300 rounded flex items-center justify-center">
              <span className="font-semibold text-slate-600 text-xs">CUSTOMER</span>
            </div>
            <div className="h-10 w-32 bg-slate-300 rounded flex items-center justify-center">
              <span className="font-semibold text-slate-600 text-xs">ENTERPRISE</span>
            </div>
          </div>
        </div>
      </section>


      {/* Unified Features Section */}
      <section id="features" className="py-10 md:py-12 bg-slate-50 scroll-mt-24" data-testid="features-section">
        <div className="container-custom">
          <div className="max-w-6xl mx-auto text-center mb-12">
            <h2 className="font-outfit text-4xl md:text-5xl font-bold tracking-tight leading-tight text-slate-900 mb-6" data-testid="features-heading">
              How TurboPay Helps
            </h2>
            {/* <p className="text-lg md:text-xl text-slate-600 leading-relaxed" data-testid="features-subheading">
              From collections to capital — we handle your entire cash cycle. <br />
              Built to help distributors collect faster and give both distributors and retailers easy access to working capital.

            </p> */}
            <div className="inline-flex items-center p-1 bg-white rounded-full shadow-sm border border-slate-200">
              <button
                onClick={() => setHowTurboPayHelps("distributors")}
                className={`px-8 py-3 rounded-full font-semibold transition-all ${howTurboPayHelps === "distributors"
                  ? "bg-[#1F497D] text-white"
                  : "text-slate-600 hover:text-slate-900"
                  }`}
                data-testid="benefits-distributors-tab"
              >
                Distributors
              </button>
              <button
                onClick={() => setHowTurboPayHelps("retailers")}
                className={`px-8 py-3 rounded-full font-semibold transition-all ${howTurboPayHelps === "retailers"
                  ? "bg-[#1F497D] text-white"
                  : "text-slate-600 hover:text-slate-900"
                  }`}
                data-testid="benefits-retailers-tab"
              >
                NBFCs & Banks
              </button>
            </div>
          </div>

          {howTurboPayHelps === "distributors" && (
            <div className="container-custom">
              <h2 className="text-center font-outfit text-4xl md:text-5xl font-bold tracking-tight leading-tight text-slate-900 mb-6">
                How TurboPay Helps Distributors
              </h2>
              <p className="text-center text-slate-600 max-w-2xl mx-auto mb-12">
                Join for collections. Stay for credit. Both compound with every transaction.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                <div className="bg-white border border-slate-100 rounded-2xl p-8 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-all duration-300 hover:-translate-y-1" data-testid="feature-card-1">
                  <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center mb-4">
                    <FileText className="h-6 w-6 text-indigo-600" />
                  </div>
                  <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-3">Stop Cash Misappropriation Instantly </h3>
                  <p className="text-slate-600 leading-relaxed">Every collection recorded by your field team is OTP-confirmed by the retailer and geo-verified at the point of collection. You know exactly who
                    collected, when, where and how much — cash leakage stops from day one.</p>
                </div>

                <div className="bg-white border border-slate-100 rounded-2xl p-8 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-all duration-300 hover:-translate-y-1" data-testid="feature-card-2">
                  <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center mb-4">
                    <CreditCard className="h-6 w-6 text-indigo-600" />
                  </div>
                  <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-3"> Every Rupee Tracked in Real Time </h3>
                  <p className="text-slate-600 leading-relaxed">Every invoice raised and every rupee collected is digitised automatically — no manual entry, no end-of-day reconciliation
                    surprises. Your entire collections cycle runs on one platform with a complete audit trail.</p>
                </div>

                <div className="bg-white border border-slate-100 rounded-2xl p-8 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-all duration-300 hover:-translate-y-1" data-testid="feature-card-3">
                  <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center mb-4">
                    <Users className="h-6 w-6 text-indigo-600" />
                  </div>
                  <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-3">Credit That Grows With Your Trade </h3>
                  <p className="text-slate-600 leading-relaxed">Your collections history on TurboPay becomes your credit profile. As your trade grows, your credit eligibility grows
                    with it — working capital based on how you actually trade, not a balance sheet reviewed once a year.</p>
                </div>

                <div className="bg-white border border-slate-100 rounded-2xl p-8 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-all duration-300 hover:-translate-y-1" data-testid="feature-card-4">
                  <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center mb-4">
                    <Zap className="h-6 w-6 text-indigo-600" />
                  </div>
                  <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-3">Digitise Your Entire Collections Operation </h3>
                  <p className="text-slate-600 leading-relaxed">Upload invoices, record collections, track outstanding amounts and reconcile payments — all in one platform.
                    Your entire collections cycle goes paperless from day one with zero disruption to existing operations.</p>
                </div>

                <div className="bg-white border border-slate-100 rounded-2xl p-8 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-all duration-300 hover:-translate-y-1" data-testid="feature-card-5">
                  <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center mb-4">
                    <TrendingUp className="h-6 w-6 text-indigo-600" />
                  </div>
                  <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-3">Full Field Team Accountability </h3>
                  <p className="text-slate-600 leading-relaxed">Track every salesperson's beat, visits and collections in real time. OTP confirmation and geo-verification on every payment ensures complete
                    accountability — no more end-of-day cash surprises or follow-up gaps.</p>
                </div>

                <div className="bg-white border border-slate-100 rounded-2xl p-8 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-all duration-300 hover:-translate-y-1" data-testid="feature-card-6">
                  <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center mb-4">
                    <RefreshCw className="h-6 w-6 text-indigo-600" />
                  </div>
                  <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-3">Automated Reconciliation</h3>
                  <p className="text-slate-600 leading-relaxed">Every invoice, collection and payment is automatically matched and reconciled — eliminating manual accounting
                    errors and saving hours of back-office work every day. Works seamlessly alongside Tally.</p>
                </div>

                <div className="bg-white border border-slate-100 rounded-2xl p-8 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-all duration-300 hover:-translate-y-1" data-testid="feature-card-7">
                  <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center mb-4">
                    <BarChart3 className="h-6 w-6 text-indigo-600" />
                  </div>
                  <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-3">Live Collections Dashboard  </h3>
                  <p className="text-slate-600 leading-relaxed">See every outstanding invoice, pending collection and completed payment in one live dashboard —
                    updated in real time so you always know exactly where your money is across your entire retailer network.</p>
                </div>

                <div className="bg-white border border-slate-100 rounded-2xl p-8 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-all duration-300 hover:-translate-y-1" data-testid="feature-card-8">
                  <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center mb-4">
                    <BarChart3 className="h-6 w-6 text-indigo-600" />
                  </div>
                  <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-3">Retailer Invoice Discounting</h3>
                  <p className="text-slate-600 leading-relaxed">Offer retailers short-term credit against their purchase invoices through TurboPay. Repayments are collected automatically
                    and settled directly with the lender — stronger retailer relationships with zero credit risk to you.</p>
                </div>

                <div className="bg-white border border-slate-100 rounded-2xl p-8 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-all duration-300 hover:-translate-y-1" data-testid="feature-card-9">
                  <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center mb-4">
                    <Shield className="h-6 w-6 text-indigo-600" />
                  </div>
                  <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-3">Working Capital Against Your Trade </h3>
                  <p className="text-slate-600 leading-relaxed">Your TurboPay collections history is your credit application. Access working capital from our NBFC partners based on your actual trade behaviour —
                    no collateral, no branch visits, no lengthy approval process.</p>
                </div>
              </div>
            </div>
          )}

          {howTurboPayHelps === "retailers" && (
            <div className="container-custom">
              <h2 className="text-center font-outfit text-4xl md:text-5xl font-bold tracking-tight leading-tight text-slate-900 mb-6">
                How TurboPay Helps Lenders
              </h2>
              <p className="text-center text-slate-600 max-w-2xl mx-auto mb-12">
                Real-time trade signals. Automated collections. Zero balance sheet risk.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                <div className="bg-white border border-slate-100 rounded-2xl p-8 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-all duration-300 hover:-translate-y-1" data-testid="feature-card-1">
                  <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center mb-4">
                    <FileText className="h-6 w-6 text-indigo-600" />
                  </div>
                  <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-3">Underwrite Against Live Trade Behaviour  </h3>
                  <p className="text-slate-600 leading-relaxed">TurboPay captures invoice-level repayment behaviour across every distributor node in real time — signals that no GST return, bureau report or annual financial statement
                    has ever provided. Underwrite with confidence.</p>
                </div>

                <div className="bg-white border border-slate-100 rounded-2xl p-8 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-all duration-300 hover:-translate-y-1" data-testid="feature-card-2">
                  <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center mb-4">
                    <CreditCard className="h-6 w-6 text-indigo-600" />
                  </div>
                  <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-3"> Collections Flow Directly Into Your Account </h3>
                  <p className="text-slate-600 leading-relaxed">When retailers pay through TurboPay, repayments are routed automatically into your designated bank account via UPI rails.
                    No manual collections effort, no chasing, no leakage — fully automated end to end.</p>
                </div>

                <div className="bg-white border border-slate-100 rounded-2xl p-8 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-all duration-300 hover:-translate-y-1" data-testid="feature-card-3">
                  <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center mb-4">
                    <Users className="h-6 w-6 text-indigo-600" />
                  </div>
                  <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-3">Risk Visibility 40 Days Earlier  </h3>
                  <p className="text-slate-600 leading-relaxed">Delinquency signals emerge at invoice level before they appear on any bureau report — giving you 40 days of early
                    warning to manage exposure before a default crystallises in your book.</p>
                </div>

                <div className="bg-white border border-slate-100 rounded-2xl p-8 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-all duration-300 hover:-translate-y-1" data-testid="feature-card-5">
                  <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center mb-4">
                    <TrendingUp className="h-6 w-6 text-indigo-600" />
                  </div>
                  <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-3">Live Underwriting Signals via API </h3>
                  <p className="text-slate-600 leading-relaxed">Connect to TurboPay's Credit Infrastructure Layer via API — access real-time repayment behaviour, cycle frequency and outstanding exposure across
                    every distributor node. Underwriting that updates every collection cycle.</p>
                </div>

                <div className="bg-white border border-slate-100 rounded-2xl p-8 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-all duration-300 hover:-translate-y-1" data-testid="feature-card-6">
                  <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center mb-4">
                    <RefreshCw className="h-6 w-6 text-indigo-600" />
                  </div>
                  <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-3">Verified Retailer Identity Network </h3>
                  <p className="text-slate-600 leading-relaxed">Every retailer on TurboPay has a verified GST number and mobile identity linked to their repayment behaviour. 15,000 retailers live today with 12,000+
                    GST and mobile verified — a credit bureau built from the ground up.</p>
                </div>

                <div className="bg-white border border-slate-100 rounded-2xl p-8 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-all duration-300 hover:-translate-y-1" data-testid="feature-card-7">
                  <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center mb-4">
                    <BarChart3 className="h-6 w-6 text-indigo-600" />
                  </div>
                  <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-3"> Automated UPI Collections</h3>
                  <p className="text-slate-600 leading-relaxed">Retailer repayments are captured through TurboPay and routed directly into your bank account via UPI rails — fully automated, zero manual
                    intervention required from your collections or operations team.</p>
                </div>

                <div className="bg-white border border-slate-100 rounded-2xl p-8 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-all duration-300 hover:-translate-y-1" data-testid="feature-card-7">
                  <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center mb-4">
                    <BarChart3 className="h-6 w-6 text-indigo-600" />
                  </div>
                  <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-3"> Dynamic Credit Limit Management  </h3>
                  <p className="text-slate-600 leading-relaxed">Credit limits update automatically as distributor trade behaviour evolves — no annual review cycles, no manual reassessments.
                    Limits grow when trade grows and tighten when signals deteriorate.</p>
                </div>

                <div className="bg-white border border-slate-100 rounded-2xl p-8 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-all duration-300 hover:-translate-y-1" data-testid="feature-card-8">
                  <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center mb-4">
                    <BarChart3 className="h-6 w-6 text-indigo-600" />
                  </div>
                  <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-3"> Zero Borrower Acquisition Cost  </h3>
                  <p className="text-slate-600 leading-relaxed">TurboPay's distributor network is your credit distribution channel. Access verified borrowers with months of live trade history — without spending
                    on field acquisition, individual appraisals or branch operations.</p>
                </div>

                <div className="bg-white border border-slate-100 rounded-2xl p-8 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-all duration-300 hover:-translate-y-1" data-testid="feature-card-9">
                  <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center mb-4">
                    <Shield className="h-6 w-6 text-indigo-600" />
                  </div>
                  <h3 className="font-outfit text-xl font-semibold text-slate-900 mb-3">Zero Balance Sheet Risk to TurboPay </h3>
                  <p className="text-slate-600 leading-relaxed">Capital always belongs to the lender. TurboPay earns a platform fee from distributors and a small infra take-rate from lenders on AUM deployed —
                    we have no lending exposure and no competing interest in your book.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Benefits Section with Tabs */}
      <section className="py-10 md:py-16 bg-slate-50" data-testid="benefits-section">
        <div className="container-custom">
          <div className="text-center mb-8">
            <h2 className="font-outfit text-4xl md:text-5xl font-bold text-slate-900 mb-6">{benefitsTab === "distributors" ? "Built for Distributors" : "Built for Institutional Lenders"}</h2>

            {/* Tab Toggle */}
            <div className="inline-flex items-center p-1 bg-white rounded-full shadow-sm border border-slate-200">
              <button
                onClick={() => setBenefitsTab("distributors")}
                className={`px-8 py-3 rounded-full font-semibold transition-all ${benefitsTab === "distributors"
                  ? "bg-[#1F497D] text-white"
                  : "text-slate-600 hover:text-slate-900"
                  }`}
                data-testid="benefits-distributors-tab"
              >
                Distributors
              </button>
              <button
                onClick={() => setBenefitsTab("retailers")}
                className={`px-8 py-3 rounded-full font-semibold transition-all ${benefitsTab === "retailers"
                  ? "bg-[#1F497D] text-white"
                  : "text-slate-600 hover:text-slate-900"
                  }`}
                data-testid="benefits-retailers-tab"
              >
                NBFCs & Banks
              </button>
            </div>
          </div>

          {/* Distributors Content */}
          {benefitsTab === "distributors" && (
            <div className="transition-all duration-300" data-testid="benefits-distributors">
              <p className="text-center text-slate-600 max-w-2xl mx-auto mb-12">
                Join for collections. Stay for credit. Both compound with every transaction.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
                  <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center mb-4">
                    <Clock className="h-5 w-5 text-white" />
                  </div>
                  <h4 className="font-outfit text-xl font-semibold text-slate-900 mb-2">Go Live in 48 Hours </h4>
                  <p className="text-slate-600 text-base">Upload your invoices, onboard your field team and start recording collections digitally — most distributors are fully live within 48 hours with zero disruption to existing operations.</p>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
                  <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center mb-4">
                    <Shield className="h-5 w-5 text-white" />
                  </div>
                  <h4 className="font-outfit text-xl font-semibold text-slate-900 mb-2"> Works With Tally and Excel </h4>
                  <p className="text-slate-600 text-base">TurboPay integrates with Tally ERP and accepts invoice uploads via Excel. No need to change your existing accounting setup — we sit alongside your current tools, not on top of them.</p>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
                  <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center mb-4">
                    <TrendingUp className="h-5 w-5 text-white" />
                  </div>
                  <h4 className="font-outfit text-xl font-semibold text-slate-900 mb-2">Your Field Team Will Love It </h4>
                  <p className="text-slate-600 text-base">The TurboPay mobile app is built for field agents with minimal digital literacy. Simple interface, OTP confirmation, geo-check — everything done at the point of collection in under 30 seconds.</p>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
                  <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center mb-4">
                    <Users className="h-5 w-5 text-white" />
                  </div>
                  <h4 className="font-outfit text-xl font-semibold text-slate-900 mb-2">No Collateral. No Branch Visits. </h4>
                  <p className="text-slate-600 text-base">When you are ready for working capital, your TurboPay trade history is your application. No property pledges, no financial statement submissions, no lengthy approval processes — credit based on how you trade.</p>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
                  <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center mb-4">
                    <BarChart3 className="h-5 w-5 text-white" />
                  </div>
                  <h4 className="font-outfit text-xl font-semibold text-slate-900 mb-2">Your Data Stays Yours</h4>
                  <p className="text-slate-600 text-base">Your invoice data, retailer records and collections history are stored securely on AWS with encryption and daily backups. You own your data and can export it at any time.</p>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
                  <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center mb-4">
                    <Zap className="h-5 w-5 text-white" />
                  </div>
                  <h4 className="font-outfit text-xl font-semibold text-slate-900 mb-2">Simple Monthly Pricing </h4>
                  <p className="text-slate-600 text-base">TurboPay charges a flat monthly platform fee per distributor node — no setup fees, no per-transaction charges, no hidden costs. Contact us at sales@turbopay.cash for current pricing.</p>
                </div>
              </div>
            </div>
          )}

          {/* Retailers Content */}
          {benefitsTab === "retailers" && (
            <div className="transition-all duration-300" data-testid="benefits-retailers">
              <p className="text-center text-slate-600 max-w-2xl mx-auto mb-12">
                Partner once. Access a live, growing network of verified distributor trade signals.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100">
                  <div className="w-10 h-10 rounded-lg bg-green-500 flex items-center justify-center mb-4">
                    <CreditCard className="h-5 w-5 text-white" />
                  </div>
                  <h4 className="font-outfit text-xl font-semibold text-slate-900 mb-2">API Integration in 2–4 Weeks </h4>
                  <p className="text-slate-600 text-base">TurboPay provides complete API documentation, a sandbox environment and a dedicated integration team. Most lenders are live on the platform within 2–4 weeks of signing the partnership agreement.</p>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100">
                  <div className="w-10 h-10 rounded-lg bg-green-500 flex items-center justify-center mb-4">
                    <Zap className="h-5 w-5 text-white" />
                  </div>
                  <h4 className="font-outfit text-xl font-semibold text-slate-900 mb-2">Pilot With a Live Network </h4>
                  <p className="text-slate-600 text-base">No need to wait for new distributor onboarding. TurboPay has 10 live distributor nodes and 15,000 retailers transacting today — pilot credit deployment against a live, verified network from day one.</p>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100">
                  <div className="w-10 h-10 rounded-lg bg-green-500 flex items-center justify-center mb-4">
                    <Shield className="h-5 w-5 text-white" />
                  </div>
                  <h4 className="font-outfit text-xl font-semibold text-slate-900 mb-2">Regulatory-Aligned Structure </h4>
                  <p className="text-slate-600 text-base">TurboPay operates with a regulatory-aligned co-lending structure designed to comply with RBI guidelines for NBFC and bank partnerships. Full documentation available under NDA.</p>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100">
                  <div className="w-10 h-10 rounded-lg bg-green-500 flex items-center justify-center mb-4">
                    <RefreshCw className="h-5 w-5 text-white" />
                  </div>
                  <h4 className="font-outfit text-xl font-semibold text-slate-900 mb-2"> Dedicated Lender Dashboard </h4>
                  <p className="text-slate-600 text-base">Access a live dashboard showing distributor repayment behaviour, outstanding exposure, cycle trends and signal quality across your entire credit book — updated in real time, no manual reporting required.</p>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100">
                  <div className="w-10 h-10 rounded-lg bg-green-500 flex items-center justify-center mb-4">
                    <TrendingUp className="h-5 w-5 text-white" />
                  </div>
                  <h4 className="font-outfit text-xl font-semibold text-slate-900 mb-2">Complete Collections Audit Trail </h4>
                  <p className="text-slate-600 text-base">Every collection is timestamped, OTP-confirmed and geo-verified — giving you a complete tamper-proof audit trail for every rupee collected against your deployed capital.</p>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100">
                  <div className="w-10 h-10 rounded-lg bg-green-500 flex items-center justify-center mb-4">
                    <Zap className="h-5 w-5 text-white" />
                  </div>
                  <h4 className="font-outfit text-xl font-semibold text-slate-900 mb-2">Ready to Pilot — Contact Us </h4>
                  <p className="text-slate-600 text-base">We are onboarding our first institutional lending partners now. If you are an NBFC or bank interested in deploying credit through TurboPay's infrastructure, contact raj@turbopay.cash to discuss a pilot.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* How It Works with Tabs */}
      <section id="how-it-works" className="py-10 md:py-16 scroll-mt-24" data-testid="how-it-works-section">
        <div className="container-custom">
          <div className="text-center mb-8">
            <h2 className="font-outfit text-4xl md:text-5xl font-bold text-slate-900 mb-6">How TurboPay Works</h2>

            {/* Tab Toggle */}
            <div className="inline-flex items-center p-1 bg-white rounded-full shadow-sm border border-slate-200">
              <button
                onClick={() => setHowItWorksTab("distributors")}
                className={`px-8 py-3 rounded-full font-semibold transition-all ${howItWorksTab === "distributors"
                  ? "bg-[#1F497D] text-white"
                  : "text-slate-600 hover:text-slate-900"
                  }`}
                data-testid="how-it-works-distributors-tab"
              >
                Distributors
              </button>
              <button
                onClick={() => setHowItWorksTab("retailers")}
                className={`px-8 py-3 rounded-full font-semibold transition-all ${howItWorksTab === "retailers"
                  ? "bg-[#1F497D] text-white"
                  : "text-slate-600 hover:text-slate-900"
                  }`}
                data-testid="how-it-works-retailers-tab"
              >
                NBFCs & Banks
              </button>
            </div>
          </div>

          {/* Distributors Steps */}
          {howItWorksTab === "distributors" && (
            <div className="transition-all duration-300">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
                <div className="bg-white border border-slate-100 rounded-xl p-6 shadow-sm" data-testid="distributor-step-1">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 text-white flex items-center justify-center font-bold text-lg mb-4">
                    <Package className="h-6 w-6" />
                  </div>
                  <div className="text-xs font-semibold text-indigo-600 mb-2">STEP 1</div>
                  <h4 className="font-outfit text-xl font-semibold text-slate-900 mb-2">Sign Up and Upload</h4>
                  <p className="text-base text-slate-600">Create your account, upload invoices via Excel or sync from Tally — your entire receivables are live on TurboPay within minutes.</p>
                </div>

                <div className="bg-white border border-slate-100 rounded-xl p-6 shadow-sm" data-testid="distributor-step-2">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 text-white flex items-center justify-center font-bold text-lg mb-4">
                    <DollarSign className="h-6 w-6" />
                  </div>
                  <div className="text-xs font-semibold text-indigo-600 mb-2">STEP 2</div>
                  <h4 className="font-outfit text-xl font-semibold text-slate-900 mb-2">Field Team Collects</h4>
                  <p className="text-base text-slate-600">Your salesperson visits the retailer and records the collection on the TurboPay app. Retailer confirms via OTP, geo-location verified automatically.</p>
                </div>

                <div className="bg-white border border-slate-100 rounded-xl p-6 shadow-sm" data-testid="distributor-step-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 text-white flex items-center justify-center font-bold text-lg mb-4">
                    <CreditCard className="h-6 w-6" />
                  </div>
                  <div className="text-xs font-semibold text-indigo-600 mb-2">STEP 3</div>
                  <h4 className="font-outfit text-xl font-semibold text-slate-900 mb-2">Signal Generated </h4>
                  <p className="text-base text-slate-600">Every confirmed collection becomes a trade signal — repayment timing, amount, cycle frequency and retailer identity captured in real time.</p>
                </div>

                <div className="bg-white border border-slate-100 rounded-xl p-6 shadow-sm" data-testid="distributor-step-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 text-white flex items-center justify-center font-bold text-lg mb-4">
                    <Zap className="h-6 w-6" />
                  </div>
                  <div className="text-xs font-semibold text-indigo-600 mb-2">STEP 4</div>
                  <h4 className="font-outfit text-xl font-semibold text-slate-900 mb-2"> Credit Profile Built </h4>
                  <p className="text-base text-slate-600">Your signals accumulate into a continuously updated credit profile. The stronger your trade history, the stronger your credit eligibility with our NBFC partners.</p>
                </div>

                <div className="bg-white border border-slate-100 rounded-xl p-6 shadow-sm" data-testid="distributor-step-5">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 text-white flex items-center justify-center font-bold text-lg mb-4">
                    <CheckCircle2 className="h-6 w-6" />
                  </div>
                  <div className="text-xs font-semibold text-indigo-600 mb-2">STEP 5</div>
                  <h4 className="font-outfit text-xl font-semibold text-slate-900 mb-2">Working Capital Unlocked </h4>
                  <p className="text-base text-slate-600">NBFC partners deploy working capital against your live trade profile — funds disbursed to your account, repayments collected automatically through TurboPay.</p>
                </div>
              </div>
            </div>
          )}

          {/* Retailers Steps */}
          {howItWorksTab === "retailers" && (
            <div className="transition-all duration-300">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
                <div className="bg-white border border-slate-100 rounded-xl p-6 shadow-sm" data-testid="retailer-step-1">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-green-600 text-white flex items-center justify-center font-bold text-lg mb-4">
                    <Package className="h-6 w-6" />
                  </div>
                  <div className="text-xs font-semibold text-green-600 mb-2">STEP 1</div>
                  <h4 className="font-outfit text-xl font-semibold text-slate-900 mb-2"> Sign Partnership Agreement </h4>
                  <p className="text-base text-slate-600">Execute a partnership and integration agreement with TurboPay — our team handles API documentation, sandbox access and regulatory framework review.</p>
                </div>

                <div className="bg-white border border-slate-100 rounded-xl p-6 shadow-sm" data-testid="retailer-step-2">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-green-600 text-white flex items-center justify-center font-bold text-lg mb-4">
                    <DollarSign className="h-6 w-6" />
                  </div>
                  <div className="text-xs font-semibold text-green-600 mb-2">STEP 2</div>
                  <h4 className="font-outfit text-xl font-semibold text-slate-900 mb-2"> Integrate via API </h4>
                  <p className="text-base text-slate-600">Connect to TurboPay's Credit Infrastructure Layer — access live distributor signal dashboards, configure credit parameters and set up automated disbursement flows.</p>
                </div>

                <div className="bg-white border border-slate-100 rounded-xl p-6 shadow-sm" data-testid="retailer-step-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-green-600 text-white flex items-center justify-center font-bold text-lg mb-4">
                    <TrendingUp className="h-6 w-6" />
                  </div>
                  <div className="text-xs font-semibold text-green-600 mb-2">STEP 3</div>
                  <h4 className="font-outfit text-xl font-semibold text-slate-900 mb-2">Review Live Signals </h4>
                  <p className="text-base text-slate-600">Access real-time repayment behaviour, cycle frequency and retailer identity data across all distributor nodes — underwrite against live trade data, not annual financials.</p>
                </div>

                <div className="bg-white border border-slate-100 rounded-xl p-6 shadow-sm" data-testid="retailer-step-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-green-600 text-white flex items-center justify-center font-bold text-lg mb-4">
                    <CreditCard className="h-6 w-6" />
                  </div>
                  <div className="text-xs font-semibold text-green-600 mb-2">STEP 4</div>
                  <h4 className="font-outfit text-xl font-semibold text-slate-900 mb-2">Deploy Credit </h4>
                  <p className="text-base text-slate-600">Approve credit limits against verified distributor profiles — disbursement automated through TurboPay's rails, tracked in real time on your lender dashboard.</p>
                </div>

                <div className="bg-white border border-slate-100 rounded-xl p-6 shadow-sm" data-testid="retailer-step-5">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-green-600 text-white flex items-center justify-center font-bold text-lg mb-4">
                    <CheckCircle2 className="h-6 w-6" />
                  </div>
                  <div className="text-xs font-semibold text-green-600 mb-2">STEP 5</div>
                  <h4 className="font-outfit text-xl font-semibold text-slate-900 mb-2">Receive Repayments </h4>
                  <p className="text-base text-slate-600">Retailer repayments flow through TurboPay's UPI collection rails directly into your designated bank account — fully automated, zero manual collections effort required.</p>
                </div>
              </div>

            </div>
          )}
        </div>
      </section>

      {/* Impact Stats Section */}
      <section className="py-10 md:py-16 bg-slate-50" data-testid="stats-section">
        <div className="container-custom">
          <h2 className="font-outfit text-4xl md:text-5xl font-bold tracking-tight leading-tight text-center mb-16 text-slate-900" data-testid="stats-heading">
            Real Business Impact
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center" data-testid="stat-1">
              <div className="text-5xl font-bold text-[#1F497D] mb-3">20</div>
              <p className="text-slate-700 font-medium">FMCG Distributors on TurboPay</p>
            </div>

            <div className="text-center" data-testid="stat-2">
              <div className="text-5xl font-bold text-[#1F497D] mb-3">25,000+</div>
              <p className="text-slate-700 font-medium">Retailers on TurboPay</p>
            </div>

            <div className="text-center" data-testid="stat-3">
              <div className="text-5xl font-bold text-[#1F497D] mb-3">1,80,000+</div>
              <p className="text-slate-700 font-medium">Invoices Collected</p>
            </div>

            <div className="text-center" data-testid="stat-4">
              <div className="text-5xl font-bold text-[#1F497D] mb-3">₹30 Crore</div>
              <p className="text-slate-700 font-medium">Collections Processed</p>
            </div>
          </div>
          <br />
          <br />
          <p className="text-center text-xl text-slate-600 max-w-5xl mx-auto mb-6">
            Launched recently and already powering thousands of daily collections across FMCG supply chains.
          </p>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-10 md:py-16 scroll-mt-24" data-testid="testimonials-section">
        <div className="container-custom">
          <h2 className="font-outfit text-4xl md:text-5xl font-bold tracking-tight leading-tight text-center mb-12 text-slate-900" data-testid="testimonials-heading">
            Loved by FMCG Businesses
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-white border border-slate-100 rounded-2xl p-8 shadow-[0_2px_8px_rgba(0,0,0,0.04)]" data-testid="testimonial-1">
              <p className="text-lg text-slate-700 leading-relaxed mb-6 italic">
                “TurboPay helped us digitize our entire collections process. We now track every invoice and payment in real time, and our daily reconciliation time has reduced drastically.”
              </p>
              <p className="text-slate-600 font-medium">SLV Distributors, Bangalore</p>
            </div>

            <div className="bg-white border border-slate-100 rounded-2xl p-8 shadow-[0_2px_8px_rgba(0,0,0,0.04)]" data-testid="testimonial-2">
              <p className="text-lg text-slate-700 leading-relaxed mb-6 italic">
                “Managing field collections was always messy. TurboPay gives us complete visibility of what each salesman collects every day. No more cash mismatches or manual tracking.”
              </p>
              <p className="text-slate-600 font-medium">Silverline Distributors, Bangalore</p>
            </div>
          </div>
        </div>
      </section>


      {/* Final CTA */}
      <section className="py-10 md:py-16 bg-gradient-to-br from-indigo-50 to-white" data-testid="final-cta-section">
        <div className="container-custom text-center">
          <h2 className="font-outfit text-4xl md:text-6xl font-bold tracking-tight leading-tight text-slate-900 mb-6" data-testid="final-cta-heading">
            Ready to Unlock Faster Cash Flow?
          </h2>
          <p className="text-lg md:text-xl text-slate-600 leading-relaxed mb-8 max-w-2xl mx-auto" data-testid="final-cta-subheading">
            Scale your FMCG business with TurboPay.
          </p>
          <Dialog open={demoDialogOpen} onOpenChange={setDemoDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#1F497D] text-white hover:bg-[#183a64] h-14 px-10 rounded-full font-semibold text-lg shadow-[0_8px_20px_0_rgba(31,73,125,0.3)] transition-all hover:scale-105 active:scale-95" data-testid="final-cta-btn">
                Get Started Today
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </DialogTrigger>
          </Dialog>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12" data-testid="footer">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row justify-between w-full items-center gap-6">
              <div className="flex flex-wrap justify-center md:justify-start gap-6 text-sm">
                {/* <button onClick={() => scrollToSection('solution')} className="hover:text-[#1F497D] transition-colors">
                Product
              </button> */}
                <button onClick={() => scrollToSection('features')} className="hover:text-[#1F497D] transition-colors">
                  Solutions
                </button>
                <Link to="/pricing" className="hover:text-[#1F497D] transition-colors">
                  Pricing
                </Link>
                <Link to="/faq" className="hover:text-[#1F497D] transition-colors">
                  FAQs
                </Link>
                <a href="#" onClick={(e) => { e.preventDefault(); setDemoStatus("contact"); setDemoDialogOpen(true); }} className="hover:text-indigo-400 transition-colors">Contact</a>
              </div>
              <div className="flex flex-wrap justify-center md:justify-end gap-6 text-sm">
                <Link to="/termsOfService" className="hover:text-[#1F497D] transition-colors">
                  Terms of Service
                </Link>
                <Link to="/privacyPolicy" className="hover:text-[#1F497D] transition-colors">
                  Privacy Policy
                </Link>
              </div>
            </div>

          <p className="text-sm text-slate-400 text-center mt-4">© 2026 TurboPay is a product of CASHPUNDIT TECHNOLOGIES PRIVATE LIMITED. All rights reserved.</p>
        </div>
      </footer>

      <Toaster />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/termsOfService" element={<TermsPage />} />
          <Route path="/privacyPolicy" element={<PrivacyPage />} />
        </Routes>
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;
